/**
 * @module engine3d/Engine3D
 * @summary Lifecycle, input and public API for the 3D view.
 * @description
 *   Mirrors the shape of the 2D `CanvasEngine` deliberately: mount onto a
 *   canvas, run a render-on-demand loop, expose imperative commands to React,
 *   and never call React back except through the existing engine event bus.
 *
 *   The 3D view is a SECOND WAY TO LOOK at the same document — it reads
 *   `S.shapes` / `S.connections` and writes nothing except the selection. Every
 *   edit still happens in 2D, so nothing here can corrupt a drawing.
 *
 *   Unlike the 2D loop this one is idle by default: a frame is drawn only when
 *   the camera moves, the document changes, or the hover target changes.
 */
import { S, on, emit, markDirty } from '../engine/state';
import { scheduleAutoSave } from '../engine/persistence';
import { computeBalance, formatRate } from '../engine/balance';
import { loadViewPrefs, renderPrefs, saveViewPrefs } from './viewPrefs';
import { GRID } from '../engine/state';
import { captureSnapshot, pushCapturedUndo } from '../engine/history';
import type { Level, PortKey, Shape } from '../types';
import { rayPlanePoint, v3, type Vec3 } from './math3';
import { Camera, wheelZoomFactor, type ProjectionKind, type ViewPreset } from './camera';
import { Renderer3D, type RenderOptions } from './renderer3d';
import { buildScene, emptyScene, type Scene, type SceneObject } from './scene';
import { hasBespokeModel } from './models';
import { buildGizmo, emptyOverlay, handlePoint, type HandleDesc, type Overlay } from './gizmo';
import * as p3 from './pipes3';
import * as d3 from './dims3';
import { axisSnap, freePoint, snapRay, type SnapResult } from './snap';
import type { BendStyle, Connection, Dim3, FlowMedium, LegNote, Pipe3, Pipe3Node } from '../types';
import {
  directionOfConnection,
  directionOfPipe3,
  mediumOfConnection,
  mediumOfPipe3,
  shapeIndex,
} from '../engine/streams';
import * as levels from './levels';
import { hasOverride, placeOf, resetPlan, setElevation, setPlan, setYaw } from './placement';
import {
  endsOf,
  flipTurnAxis,
  applyHandle,
  isRerouted,
  resetRoute3,
  addBend,
  removeBend,
  moveBend,
  bendCount,
  routePipe3,
} from './pipes';
import { isStructureVisible, setStructureVisible, setLevelStructure, levelHasStructure } from './structure';
import type { DocSnapshot } from '../engine/state';

export interface View3DStats {
  shapes: number;
  pipes: number;
  triangles: number;
  /** How many distinct symbol types have a hand-built model. */
  modelled: number;
  fps: number;
  levels: number;
}

/**
 * Coarse yaw step for the rotate ring, in degrees.
 *
 * Fifteen rather than ninety: equipment is usually square to the line, but a
 * transfer chute or a skewed conveyor needs the angles in between, and 15°
 * divides both 90 and 360 so the square cases stay exactly reachable.
 */
const YAW_STEP = 15;

/** What the HUD needs to know about the current selection. */
export interface Selection3D {
  kind: 'none' | 'shape' | 'conn' | 'mixed';
  count: number;
  label: string;
  /** Level id shared by every selected shape, or '' when they differ. */
  levelId: string;
  /** Absolute height of the first selected item. */
  elevation: number;
  /** True when the selection is an automatic per-leg figure, not a placed dimension. */
  isLeg: boolean;
  /** True when that leg's length can be typed in; see `legBlockedReason`. */
  legDrivable: boolean;
  /** True when the selected leg is one whose figure has been switched off. */
  legHidden: boolean;
  /** True when something in the selection has been moved in 3D. */
  moved: boolean;
  /** True when a selected pipe run has been re-routed by hand. */
  rerouted: boolean;
  /** Explicit bends on the first selected run. */
  bends: number;
  /** True when the selection is a pipe drawn in 3D rather than a 2D connector. */
  isPipe3: boolean;
  /** Elbow radius on the selected run. */
  bend: BendStyle;
  /** False when a 2D connector's pipe is hidden in this view. */
  shownIn3D: boolean;
  /** True when every selected shape is hidden in 3D. */
  hidden: boolean;
  /** True when every selected shape has its platform suppressed. */
  noStruct: boolean;
  /**
   * The 3D model every selected shape is pinned to, '' when they differ or when
   * none is pinned and the form is being inferred.
   */
  model3: string;
  /** Total developed length of the selected runs, in world units. */
  runLength: number;
  /** True when the selection is a hand-placed dimension. */
  isDim: boolean;
  /** Its own text, or '' when it is showing the measured length. */
  dimText: string;
  /** Straight-line distance it measures, in world units. */
  dimLength: number;
  /** Which side its line sits on. */
  dimFlip: boolean;
  /** True when the selection is a zone box. */
  isZone: boolean;
  /** How that zone box is drawn: floor area, wall, or room. */
  zoneMode: 'floor' | 'wall' | 'room';
  /** Which edge a wall stands on. */
  wallSide: PortKey;
  /** Height of a wall or room. */
  wallH: number;
  /** Colour of the selected pipe in 3D, or '' when it has none of its own. */
  pipeColor: string;
  /** True when that colour is a 3D override rather than inherited from 2D. */
  pipeColorOwn: boolean;
  /** What the first selected run is PINNED to carry, or 'auto' when it is not. */
  flowPinned: FlowMedium | 'auto';
  /** What it actually flows as right now — the pin, or what the drawing implies. */
  flowMedium: FlowMedium;
  /** True when it has been turned round, against the way it was drawn. */
  flowReversed: boolean;
  /**
   * Id of the first selected equipment item, '' when the selection is not one.
   *
   * The formulation controls edit a SHAPE rather than the selection as a whole
   * — what a feeder doses is per feeder — so the bar needs to be able to name
   * the one it is showing.
   */
  shapeId: string;
  /** Throughput typed on the first selected item, kg/h; 0 when it carries none. */
  rate: number;
  /**
   * What the balance makes of the first selected item, already formatted: the
   * kg/h on a run, or what an item passes. '' when nothing has a rate yet.
   */
  rateReadout: string;
}

/** What the pointer is doing. */
export type Tool3D = 'select' | 'pipe' | 'dim';

/** One decimal place: enough for placement, short enough to keep JSON small. */
const round1 = (v: number): number => Math.round(v * 10) / 10;

/** A pipe run being drawn, click by click. */
interface Draft {
  /** Where it started — a nozzle, a tee, or open space. */
  from: Pipe3Node;
  /** Committed vertices after the start. */
  pts: { x: number; y: number; z: number }[];
  /** The snapped point under the cursor. */
  preview: SnapResult | null;
  /**
   * The loose end this run was picked up from, when the first click landed on
   * one. The drawn chain is appended to that run on commit rather than
   * becoming a pipe of its own.
   */
  extend?: p3.EndRef;
}

/** A drag in progress on one of the gizmo handles. */
interface HandleDrag {
  handle: HandleDesc;
  grab: { x: number; y: number; z: number };
  /** Absolute start position AND yaw of every shape being moved. */
  starts: { s: Shape; x: number; y: number; z: number; yaw: number }[];
  snapshot: DocSnapshot;
  moved: boolean;
}

export type View3DListener = () => void;

/**
 * One view to capture for the PDF portfolio. `'current'` means "whatever the
 * camera is on right now, untouched" — a framing the engineer chose deliberately
 * and that re-fitting would destroy.
 */
export interface CaptureSpec {
  preset: ViewPreset | 'current';
  /** Printed as the caption under the view. */
  label: string;
}

/** A rendered view, ready for `pdf/views.ts` to place on a sheet. */
export interface CapturedView {
  label: string;
  /** `data:image/png;base64,…`. */
  png: string;
  wPx: number;
  hPx: number;
}

class Engine3D {
  private canvas: HTMLCanvasElement | null = null;
  private wrap: HTMLElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private ro: ResizeObserver | null = null;
  private raf = 0;
  private mounted = false;
  private detachers: Array<() => void> = [];

  private renderer = new Renderer3D();
  private scene: Scene = emptyScene();
  private dirty = true;
  private sceneDirty = true;

  readonly camera = new Camera();

  /**
   * View toggles, surfaced in the 3D HUD. These belong to the PERSON, not the
   * drawing — they are remembered per account, and are not written into the
   * file. Scale and vertical exaggeration are the exception and live on the
   * document (`S.scaleMm` / `S.heightScale`), because they change what the
   * drawing says rather than how one viewer happens to be looking at it.
   */
  opts: Omit<
    RenderOptions,
    'selected' | 'hoveredId' | 'scaleMm' | 'heightScale' | 'seams' | 'flow'
  > = {
    ...renderPrefs(),
    isolate: false,
  };

  /**
   * The flow animation.
   *
   * Off is the resting state and it is deliberately NOT remembered between
   * sessions. Like Isolate, this is a momentary "show me the plant running" —
   * coming back tomorrow to a drawing that is already moving, with no memory of
   * having asked for it, would be baffling.
   */
  private flowOn = false;
  /** Multiplier on both streams' speeds; 1 is the designed rate. */
  private flowSpeed = 1;
  /** Elapsed animation seconds — what every slug's position is a function of. */
  private flowTravel = 0;
  /** Wall clock at the last flow tick, or 0 when the animation is not running. */
  private flowClock = 0;

  private overlay: Overlay = emptyOverlay();
  private tool: Tool3D = 'select';
  private axisMode: p3.AxisMode = 'auto';
  private draft: Draft | null = null;
  /** Elbow radius applied to newly drawn runs. */
  private newBend: BendStyle = 'long';
  private newService = false;
  private hoveredHandle = -1;
  private drag: HandleDrag | null = null;
  private hoveredId: string | null = null;
  private lastFrame = 0;
  private fps = 0;
  private listeners = new Set<View3DListener>();

  // ── Lifecycle ──────────────────────────────────────────────────────────

  mount(canvas: HTMLCanvasElement, wrap: HTMLElement): void {
    if (this.mounted) this.unmount();
    this.canvas = canvas;
    this.wrap = wrap;
    this.ctx = canvas.getContext('2d');
    if (this.ctx) this.renderer.attach(this.ctx);
    this.mounted = true;

    this.resize();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(wrap);
    window.addEventListener('resize', this.onWindowResize);

    this.attachInput(canvas);
    // Any document or selection change invalidates the compiled scene.
    this.detachers.push(on('doc', () => this.invalidate()));
    this.detachers.push(on('history', () => this.invalidate()));
    this.detachers.push(on('selection', () => this.markDirty()));

    this.rebuild();
    this.fitView();
    this.loop();
  }

  unmount(): void {
    if (!this.mounted) return;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.detachers.forEach((d) => d());
    this.detachers = [];
    this.ro?.disconnect();
    this.ro = null;
    window.removeEventListener('resize', this.onWindowResize);
    window.clearTimeout(this.qualityTimer);
    this.quality = 1;
    this.canvas = null;
    this.wrap = null;
    this.ctx = null;
    this.mounted = false;
  }

  private onWindowResize = () => this.resize();

  /**
   * Render scale. Dropped while the camera is moving and restored the moment it
   * stops: a full plant is tens of thousands of triangles, and every one of
   * them is a canvas fill, so resolution is the cheapest thing to trade for a
   * smooth orbit. The still frame — the one anybody actually looks at, and the
   * one that gets exported — is always full resolution.
   */
  private quality = 1;
  private qualityTimer = 0;

  private setQuality(q: number): void {
    if (this.quality === q) return;
    this.quality = q;
    // One backing-store reallocation per gesture, not one per frame.
    this.resize();
  }

  private beginInteract(): void {
    window.clearTimeout(this.qualityTimer);
    this.setQuality(0.65);
  }

  private endInteract(): void {
    window.clearTimeout(this.qualityTimer);
    this.qualityTimer = window.setTimeout(() => this.setQuality(1), 150);
  }

  resize(): void {
    const { wrap, canvas } = this;
    if (!wrap || !canvas) return;
    const dpr = (window.devicePixelRatio || 1) * this.quality;
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
    this.renderer.resize(w, h, dpr);
    this.markDirty();
  }

  private loop = (): void => {
    // The flow is the one thing in this view that changes without anybody
    // touching the drawing, so it drives the frame itself rather than waiting
    // to be marked dirty by an edit or a gesture.
    if (this.flowOn) {
      const now = performance.now();
      // A backgrounded tab comes back with an enormous gap. Capped, so the
      // slugs carry on from where they were instead of teleporting halfway
      // down the plant the moment you look at it again.
      const dt = this.flowClock ? Math.min(0.1, (now - this.flowClock) / 1000) : 0;
      this.flowClock = now;
      this.flowTravel += dt * this.flowSpeed;
      this.dirty = true;
    }
    if (this.dirty) {
      if (this.sceneDirty) this.rebuild();
      const now = performance.now();
      if (this.lastFrame) {
        const dt = now - this.lastFrame;
        // Smooth the readout; a raw per-frame figure is unreadable.
        this.fps = this.fps ? this.fps * 0.85 + (1000 / Math.max(1, dt)) * 0.15 : 1000 / Math.max(1, dt);
      }
      this.lastFrame = now;
      // Handles are sized in screen space, so they are rebuilt with the frame
      // rather than cached — a few hundred triangles against the scene's tens
      // of thousands.
      this.overlay = buildGizmo(this.camera, this.wrap?.clientHeight || 600, {
        hovered: this.hoveredHandle,
        drawing: this.draft
          ? {
              pts: this.draftPoints(),
              preview: this.draft.preview ? this.draft.preview.p : null,
              radius: this.newService ? 1.7 : p3.DEFAULT_R,
            }
          : undefined,
      });
      // While the camera is moving, drop the passes that decorate the plant
      // rather than describe it. Each costs work proportional to the number of
      // objects — an offscreen stamp and a full-surface blur for shadows, an
      // occupancy map and a seat search per label for the name plates — and
      // none of it is legible at orbit speed. The seam seal goes with them: it
      // is a second rasterisation of every triangle in the scene, which is the
      // single biggest line in the frame. All of it returns with full
      // resolution the moment the gesture ends.
      const moving = this.quality < 1;
      this.renderer.render(
        this.scene,
        this.camera,
        {
          ...this.opts,
          shadows: this.opts.shadows && !moving,
          labels: this.opts.labels && !moving,
          tags: this.opts.tags && !moving,
          seams: !moving && this.sealSeams(),
          flow: this.flowOn ? { travel: this.flowTravel } : null,
          scaleMm: S.scaleMm,
          heightScale: S.heightScale,
          selected: new Set(S.selected),
          hoveredId: this.hoveredId,
          draftPts: this.draftPolyline(),
        },
        this.overlay,
      );
      this.dirty = false;
      this.notifyIfChanged();
    } else {
      this.lastFrame = 0;
    }
    this.raf = requestAnimationFrame(this.loop);
  };

  /**
   * A plant big enough that sealing the seams costs more than it is worth
   * WHILE THE FLOW IS RUNNING.
   *
   * Chosen from the triangle count rather than from the measured frame rate on
   * purpose. Rate-based switching oscillates — drop the seal, the rate
   * recovers, put it back, the rate falls — and the picture visibly flickers
   * between the two. The triangle count holds still for as long as the drawing
   * does, so a drawing is either sealed or it is not.
   */
  private static readonly SEAM_BUDGET = 24000;

  /**
   * Whether to stroke every triangle in its own fill colour, sealing the
   * hairline that canvas leaves between adjacent polygons.
   *
   * It is a SECOND full rasterisation of the scene, and on a running plant it
   * is comfortably half the frame. On a small drawing that still leaves room
   * to spare, so it stays on and the animation loses nothing. On a big one the
   * choice is between a sub-pixel seam and a stuttering animation, and the
   * seam is the lesser of those — it comes straight back the moment the flow
   * is stopped.
   */
  private sealSeams(): boolean {
    return !this.flowOn || this.scene.count <= Engine3D.SEAM_BUDGET;
  }

  private markDirty(): void {
    this.dirty = true;
  }

  /** Mark the compiled scene stale — it rebuilds before the next frame. */
  invalidate(): void {
    this.sceneDirty = true;
    this.dirty = true;
    this.docRev++;
  }

  private rebuild(): void {
    // A loaded document may already contain drawn pipes; keep new ids clear of
    // the ones it brought with it.
    p3.syncPipeCounter();
    d3.syncDimCounter();
    // The pipework is swept into finer sections while the flow is running, so
    // there is something for a slug to be drawn across. That is a property of
    // the GEOMETRY, not of the frame, which is why starting and stopping the
    // flow rebuilds the scene rather than just repainting it.
    this.scene = buildScene(S.scaleMm, this.flowOn);
    this.sceneDirty = false;
  }

  // ── Input ──────────────────────────────────────────────────────────────

  private attachInput(canvas: HTMLCanvasElement): void {
    let dragging: 'orbit' | 'pan' | null = null;
    let lx = 0;
    let ly = 0;
    let moved = 0;

    const localPos = (e: { clientX: number; clientY: number }) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onDown = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      // The pipe tool takes plain left clicks; orbit stays on the other buttons
      // so the view is still reachable mid-run.
      if (this.tool === 'pipe' && e.button === 0 && !e.shiftKey) {
        const p = localPos(e);
        this.pipeClick(p.x, p.y);
        dragging = null;
        return;
      }
      if (this.tool === 'dim' && e.button === 0 && !e.shiftKey) {
        const p = localPos(e);
        // Between dimensions, a click on one you already placed PICKS IT UP
        // rather than starting another on top of it. Without this there is no
        // way to reach an existing dimension without first leaving the tool,
        // which is how one ends up feeling undeletable. Mid-draft the click is
        // the second point and nothing else.
        if (!this.dimDraft) {
          const hit = this.renderer.pickDim(p.x, p.y);
          if (hit && !d3.isLegId(hit)) {
            S.selected = [hit];
            emit('selection');
            this.markDirty();
            this.notify();
            dragging = null;
            return;
          }
        }
        this.dimClick(p.x, p.y);
        dragging = null;
        return;
      }
      // A gizmo handle under the cursor wins over the camera: the whole point
      // of the handle is that grabbing it moves the equipment, not the view.
      if (e.button === 0 && !e.shiftKey) {
        const p = localPos(e);
        const hi = this.renderer.pickOverlay(p.x, p.y);
        const h = hi >= 0 ? this.overlay.handles[hi] : undefined;
        if (h?.kind === 'leg') {
          // A leg marker is a button, not a grip: one click removes that leg.
          this.deleteLeg(h.targetId, h.wp ?? 0);
          dragging = null;
          return;
        }
        if (hi >= 0 && this.startHandleDrag(hi, p.x, p.y)) {
          dragging = null;
          canvas.style.cursor = 'grabbing';
          return;
        }
      }
      // Middle button, right button or a held Shift pans; anything else orbits.
      dragging = e.button === 1 || e.button === 2 || e.shiftKey ? 'pan' : 'orbit';
      this.beginInteract();
      lx = e.clientX;
      ly = e.clientY;
      moved = 0;
      canvas.style.cursor = dragging === 'pan' ? 'grabbing' : 'move';
    };

    const onMove = (e: PointerEvent) => {
      const p = localPos(e);
      if (this.drag) {
        this.updateHandleDrag(p.x, p.y);
        return;
      }
      if (this.tool === 'pipe' && !dragging) {
        this.updatePipePreview(p.x, p.y);
        return;
      }
      if (this.tool === 'dim' && !dragging) {
        this.updateDimPreview(p.x, p.y);
        return;
      }
      if (!dragging) {
        // Handles take priority in hover too, so the cursor tells you what a
        // press is about to do.
        const hi = this.renderer.pickOverlay(p.x, p.y);
        if (hi !== this.hoveredHandle) {
          this.hoveredHandle = hi;
          this.markDirty();
        }
        if (hi >= 0) {
          canvas.style.cursor = 'grab';
          if (this.hoveredId !== null) {
            this.hoveredId = null;
            this.markDirty();
          }
          return;
        }
        const hit = this.renderer.pick(p.x, p.y, this.scene);
        const id = hit && hit.kind !== 'structure' ? hit.id : null;
        if (id !== this.hoveredId) {
          this.hoveredId = id;
          canvas.style.cursor = id ? 'pointer' : 'default';
          this.markDirty();
        }
        return;
      }
      const dx = e.clientX - lx;
      const dy = e.clientY - ly;
      lx = e.clientX;
      ly = e.clientY;
      moved += Math.abs(dx) + Math.abs(dy);
      if (dragging === 'orbit') this.camera.orbit(-dx * 0.007, dy * 0.007);
      else this.camera.pan(dx, dy, this.wrap?.clientHeight || 600);
      this.markDirty();
    };

    const onUp = (e: PointerEvent) => {
      if (this.drag) {
        this.endHandleDrag();
        canvas.style.cursor = 'default';
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {
          /* pointer already released */
        }
        return;
      }
      const wasDragging = dragging;
      dragging = null;
      this.endInteract();
      canvas.style.cursor = 'default';
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
      // A press that didn't travel is a click: select what is under it.
      if (wasDragging === 'orbit' && moved < 5) {
        const p = localPos(e);
        // A dimension is a screen-space annotation, so it is picked the same
        // way it is drawn — before anything is ray-cast into the plant.
        const dimId = this.renderer.pickDim(p.x, p.y);
        if (dimId) {
          S.selected = e.shiftKey || e.ctrlKey ? [...new Set([...S.selected, dimId])] : [dimId];
          markDirty();
          emit('selection');
          this.markDirty();
          return;
        }
        // Repeated clicks in the same place step through whatever is stacked
        // there, nearest first. The steelwork is scenery and is skipped.
        const hit = this.pickCycling(p.x, p.y);
        this.select(hit, e.shiftKey || e.ctrlKey);
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      this.beginInteract();
      const p = localPos(e);
      this.wheelZoom(p.x, p.y, e.deltaY, e.deltaMode);
      // A wheel has no "up" event; settle back to full resolution on idle.
      this.endInteract();
    };

    const onContext = (e: Event) => e.preventDefault();

    // Two-finger pinch to zoom, one finger to orbit — the same grammar the 2D
    // canvas uses on iPad, so the gesture doesn't change between views.
    let pinch = 0;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinch = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinch) {
        e.preventDefault();
        const d = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
        if (d > 0) {
          this.beginInteract();
          const r = canvas.getBoundingClientRect();
          const cxp = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
          const cyp = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
          this.camera.zoomAt(this.pointUnderCursor(cxp, cyp), pinch / d);
          pinch = d;
          this.markDirty();
          this.endInteract();
        }
      }
    };
    const onTouchEnd = () => {
      pinch = 0;
    };

    // Keyboard. The 2D canvas's shortcuts are suspended while this view is up
    // (see AppShell), so these keys are unambiguous.
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      // Never steal keys from the HUD's own text and number fields.
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey) return;

      if (e.key === 'Escape') {
        if (this.dimDraft) this.cancelDim();
        else if (this.draft) this.cancelPipe();
        else if (this.tool !== 'select') this.setTool('select');
        else if (S.selected.length) this.select(null, false);
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter' && this.draft) {
        this.finishPipe();
        e.preventDefault();
        return;
      }
      if ((e.key === 'p' || e.key === 'P') && !this.draft) {
        this.setTool(this.tool === 'pipe' ? 'select' : 'pipe');
        e.preventDefault();
        return;
      }
      if (e.key === 'i' || e.key === 'I') {
        this.toggle('isolate');
        this.notify();
        e.preventDefault();
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        this.toggleFlow();
        e.preventDefault();
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        if (S.selected.length) this.fitSelection();
        else this.fitView();
        e.preventDefault();
        return;
      }
      if ((e.key === 'd' || e.key === 'D') && !this.draft && !this.dimDraft) {
        this.setTool(this.tool === 'dim' ? 'select' : 'dim');
        e.preventDefault();
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Annotations first, then pipework. Deleting equipment belongs to the
        // 2D drawing that owns it; doing it from here would silently edit the
        // schematic.
        if (this.deleteSelectedDims() || this.deleteSelectedPipes()) e.preventDefault();
        return;
      }

      // Arrow keys place the selection. Alt gives a fine step; Shift swaps the
      // vertical axis in, so a laptop without PageUp can still raise things.
      const step = e.altKey ? 5 : GRID;
      let dx = 0;
      let dy = 0;
      let dz = 0;
      switch (e.key) {
        case 'ArrowLeft': dx = -step; break;
        case 'ArrowRight': dx = step; break;
        case 'ArrowUp': if (e.shiftKey) dy = step; else dz = -step; break;
        case 'ArrowDown': if (e.shiftKey) dy = -step; else dz = step; break;
        case 'PageUp': dy = step; break;
        case 'PageDown': dy = -step; break;
        default: return;
      }
      if (this.nudge(dx, dy, dz)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    this.detachers.push(() => window.removeEventListener('keydown', onKey));

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    const onDblClick = (e: MouseEvent) => {
      if (this.tool !== 'select') return;
      e.preventDefault();
      const p = localPos(e);
      this.focusAt(p.x, p.y);
    };
    canvas.addEventListener('dblclick', onDblClick);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContext);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);

    this.detachers.push(() => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('dblclick', onDblClick);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onContext);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    });
  }

  /** Selecting in 3D updates the shared selection, so 2D agrees when you switch back. */
  private select(hit: SceneObject | null, additive: boolean): void {
    if (!hit) {
      if (S.selected.length) {
        S.selected = [];
        markDirty();
        emit('selection');
        this.markDirty();
      }
      return;
    }
    if (additive) {
      S.selected = S.selected.includes(hit.id)
        ? S.selected.filter((x) => x !== hit.id)
        : [...S.selected, hit.id];
    } else {
      S.selected = [hit.id];
    }
    markDirty();
    emit('selection');
    this.markDirty();
  }

  // ── Dragging a handle ──────────────────────────────────────────────────

  /** Snap a plan coordinate to the same grid the 2D canvas uses. */
  private snapPlan(v: number): number {
    return S.snapOn ? Math.round(v / GRID) * GRID : Math.round(v * 10) / 10;
  }

  /**
   * Snap a yaw to the nearest useful angle, under the same toggle that governs
   * position. Equipment is nearly always square to the line or turned across
   * it, so the coarse step is what you want almost every time; the whole point
   * of tying it to `snapOn` is that turning snapping off frees BOTH.
   */
  private snapYaw(deg: number): number {
    return S.snapOn ? Math.round(deg / YAW_STEP) * YAW_STEP : Math.round(deg);
  }

  private rayAt(px: number, py: number) {
    return this.camera.rayFrom(px, py, this.wrap?.clientWidth || 800, this.wrap?.clientHeight || 600);
  }

  /**
   * Begin a drag on handle `hi`.
   *
   * The snapshot is taken here but only PUSHED if the drag actually moves
   * something, so a stray click on a handle doesn't litter the undo stack.
   */
  private startHandleDrag(hi: number, px: number, py: number): boolean {
    const handle = this.overlay.handles[hi];
    if (!handle) return false;
    const r = this.rayAt(px, py);
    const grab = handlePoint(handle, r.o, r.d);
    if (!grab) return false;
    // Grabbing a pinned dimension end detaches it first: dragging it plainly
    // means "put it somewhere else", and it cannot move while still anchored.
    if (handle.owner === 'dim' && handle.wp !== undefined) {
      const dim = d3.findDim3(handle.targetId);
      if (dim) d3.detachEnd(dim, handle.wp as 0 | 1);
    }
    const starts = S.selected
      .map((id) => S.shapes.find((s) => s.id === id))
      .filter((s): s is Shape => !!s && !s.locked)
      .map((s) => {
        const p = placeOf(s);
        return { s, x: p.x, y: p.y, z: p.z, yaw: p.yaw };
      });
    this.drag = { handle, grab, starts, snapshot: captureSnapshot(), moved: false };
    this.beginInteract();
    return true;
  }

  private updateHandleDrag(px: number, py: number): void {
    const d = this.drag;
    if (!d) return;
    const r = this.rayAt(px, py);
    const p = handlePoint(d.handle, r.o, r.d);
    if (!p) return;
    const dx = p.x - d.grab.x;
    const dy = p.y - d.grab.y;
    const dz = p.z - d.grab.z;

    if (d.handle.wp !== undefined) {
      // Moving a bend or a drawn-pipe vertex. Everything is measured from
      // `handle.origin`, which is where the vertex sat when the drag started —
      // measuring from its CURRENT position would re-apply the whole delta on
      // every mouse-move and send it off the drawing.
      const base = d.handle.origin;
      const k = d.handle.kind;
      const to = {
        x: k === 'y' || k === 'z' ? base.x : this.snapPlan(base.x + dx),
        y: k === 'x' || k === 'z' ? base.y : Math.round((base.y + dy) * 10) / 10,
        z: k === 'y' || k === 'x' ? base.z : this.snapPlan(base.z + dz),
      };
      if (d.handle.owner === 'dim') {
        const dim = d3.findDim3(d.handle.targetId);
        if (dim) d3.moveDimEnd(dim, d.handle.wp as 0 | 1, to);
      } else if (d.handle.owner === 'pipe3') {
        const q = p3.findPipe3(d.handle.targetId);
        if (q) p3.movePipe3Point(q, d.handle.wp, to);
      } else {
        const cn = S.connections.find((c) => c.id === d.handle.targetId);
        if (cn) moveBend(cn, d.handle.wp, to);
      }
    } else if (d.handle.kind === 'pipe') {
      const cn = S.connections.find((c) => c.id === d.handle.targetId);
      if (cn) {
        const byId = new Map<string, Shape>();
        S.shapes.forEach((s) => byId.set(s.id, s));
        const ends = endsOf(cn, byId);
        if (ends) applyHandle(cn, ends[0], ends[1], p);
      }
    } else {
      for (const st of d.starts) {
        switch (d.handle.kind) {
          case 'y':
            // Elevation snaps to the nearest floor; `setElevation` keeps any
            // remainder as an offset, so a plinth or a slung hopper survives.
            setElevation(st.s, st.y + dy);
            break;
          case 'x':
            setPlan(st.s, this.snapPlan(st.x + dx), st.z);
            break;
          case 'z':
            setPlan(st.s, st.x, this.snapPlan(st.z + dz));
            break;
          case 'plan':
            setPlan(st.s, this.snapPlan(st.x + dx), this.snapPlan(st.z + dz));
            break;
          case 'yaw': {
            // Angle swept about the ring's centre, not a linear delta: measured
            // from where the ring was GRABBED, so the machine tracks the cursor
            // exactly instead of drifting by the whole sweep on every move.
            //
            // `rot3` in radians is the world XZ bearing of the model's local +X
            // axis, which is the same measure atan2(z, x) reads here — so the
            // swept angle can be added to the starting yaw directly.
            const o = d.handle.origin;
            const swept =
              Math.atan2(p.z - o.z, p.x - o.x) - Math.atan2(d.grab.z - o.z, d.grab.x - o.x);
            setYaw(st.s, this.snapYaw(st.yaw + (swept * 180) / Math.PI));
            break;
          }
        }
      }
    }
    d.moved = true;
    this.invalidate();
  }

  private endHandleDrag(): void {
    const d = this.drag;
    this.drag = null;
    this.endInteract();
    if (!d) return;
    if (d.moved) {
      pushCapturedUndo(d.snapshot);
      emit('doc');
    }
    this.invalidate();
  }


  // ── Pipe tool ──────────────────────────────────────────────────────────

  /** The working plane a free click lands on: the last point's height. */
  private draftPlaneY(): number {
    const pts = this.draftPoints();
    if (pts.length) return pts[pts.length - 1].y;
    return S.levels[0]?.elevation ?? 0;
  }

  /**
   * The run being drawn, start to cursor.
   *
   * The committed vertices plus wherever the preview currently sits, which is
   * what makes the leg under the cursor measurable before it is placed.
   */
  draftPolyline(): Vec3[] | undefined {
    if (!this.draft) return undefined;
    const pts = this.draftPoints();
    if (!pts.length) return undefined;
    const pv = this.draft.preview;
    return pv ? [...pts, pv.p] : pts;
  }

  /**
   * Total length of the run being drawn, in millimetres, or -1 when idle.
   * The HUD shows it while drawing so the figure is available without having to
   * find it on the drawing.
   */
  draftTotalMm(): number {
    const line = this.draftPolyline();
    if (!line || line.length < 2) return -1;
    let t = 0;
    for (let i = 1; i < line.length; i++)
      t += Math.hypot(line[i].x - line[i - 1].x, line[i].y - line[i - 1].y, line[i].z - line[i - 1].z);
    return t * S.scaleMm;
  }

  /**
   * Length of the leg under the cursor, in millimetres, or -1 when there is
   * none. This is the number a person is actually watching while they drag.
   */
  draftLegMm(): number {
    const line = this.draftPolyline();
    if (!line || line.length < 2) return -1;
    const a = line[line.length - 2];
    const b = line[line.length - 1];
    return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) * S.scaleMm;
  }

  /** Every committed vertex of the run being drawn, including its start. */
  private draftPoints(): Vec3[] {
    if (!this.draft) return [];
    const start = p3.resolveNode(this.draft.from);
    const out: Vec3[] = [];
    if (start) out.push(start.p);
    out.push(...this.draft.pts);
    return out;
  }

  private snapAt(px: number, py: number, noPorts = false): SnapResult {
    const r = this.rayAt(px, py);
    return snapRay(r.o, r.d, this.camera, this.wrap?.clientHeight || 600, {
      planeY: this.draftPlaneY(),
      grid: S.snapOn ? GRID : 0,
      noPorts,
    });
  }

  /**
   * Place a free point relative to the last one.
   *
   * A nozzle or a tee is a real place and is taken exactly as found. Everything
   * else is either locked onto one world axis — chosen from where the cursor is
   * on SCREEN, which is the only way a vertical leg can be drawn at all — or,
   * in diagonal mode, taken from the camera-facing plane.
   */
  private placeFree(last: Vec3, px: number, py: number): Vec3 {
    const r = this.rayAt(px, py);
    const W = this.wrap?.clientWidth || 800;
    const H = this.wrap?.clientHeight || 600;
    const grid = S.snapOn ? GRID : 0;
    if (this.axisMode === 'diagonal') return freePoint(last, r.o, r.d, this.camera, grid);
    return axisSnap(last, r.o, r.d, this.camera, W, H, px, py, this.axisMode, grid);
  }

  /** Snap under the cursor, then place it relative to the last point. */
  private updatePipePreview(px: number, py: number): void {
    if (!this.draft) return;
    const hit = this.snapAt(px, py);
    const pts = this.draftPoints();
    if (pts.length && hit.kind === 'plane') hit.p = this.placeFree(pts[pts.length - 1], px, py);
    this.draft.preview = hit;
    this.markDirty();
  }

  /** A click while the pipe tool is running: start, extend, or finish a run. */
  private pipeClick(px: number, py: number): void {
    const hit = this.snapAt(px, py);
    if (!this.draft) {
      // Picking up a loose end continues that run instead of starting a second
      // one alongside it. The start is the end point itself, not a tee onto it.
      const grab = hit.kind === 'end' ? hit.endOf : undefined;
      this.draft = {
        from: grab ? { p: { x: round1(hit.p.x), y: round1(hit.p.y), z: round1(hit.p.z) } } : this.nodeFrom(hit),
        pts: [],
        preview: null,
        extend: grab,
      };
      this.updatePipePreview(px, py);
      this.notify();
      return;
    }
    const d = this.draft;
    const pts = this.draftPoints();
    const last = pts[pts.length - 1];
    let p = hit.p;
    if (hit.kind === 'plane' && last) p = this.placeFree(last, px, py);
    // A click that didn't move anywhere would add a zero-length leg.
    if (last && Math.hypot(p.x - last.x, p.y - last.y, p.z - last.z) < 1) return;

    // Landing on a nozzle or an existing run terminates the pipe there — and
    // landing on another run's LOOSE END swallows that run into this one, so
    // what the engineer sees is a single line with a bend, not two lines that
    // happen to touch.
    if (hit.kind !== 'plane') {
      // Landing back on the run being continued would anchor it to itself,
      // which resolves to nothing. A run has one start and one end and cannot
      // be closed into a loop, so finish at the point instead.
      const self = d.extend && hit.pipeId === d.extend.pipeId;
      this.commitDraft(
        self ? { p: { x: round1(p.x), y: round1(p.y), z: round1(p.z) } } : this.nodeFrom(hit),
        !self && hit.kind === 'end' ? hit.endOf : undefined,
      );
      return;
    }
    this.draft.pts.push({ x: round1(p.x), y: round1(p.y), z: round1(p.z) });
    this.updatePipePreview(px, py);
    this.notify();
  }

  private nodeFrom(hit: SnapResult): Pipe3Node {
    if (hit.kind === 'port') return { shapeId: hit.shapeId, port: hit.port };
    // A loose end anchors like any other point on the run it belongs to, so a
    // dimension pinned there follows the pipe instead of drifting off it. Only
    // the pipe tool treats an end as somewhere to carry on from, and it builds
    // its own node for that.
    if (hit.kind === 'pipe' || hit.kind === 'end') return { pipeId: hit.pipeId, t: hit.t };
    return { p: { x: round1(hit.p.x), y: round1(hit.p.y), z: round1(hit.p.z) } };
  }

  /**
   * Turn the draft into pipework.
   *
   * Not always a NEW pipe: a run picked up from a loose end grows, and one
   * drawn onto another's loose end absorbs it. Either way the engineer ends up
   * with one run through the joint, with a proper elbow where the old end was.
   */
  private commitDraft(to: Pipe3Node, land?: p3.EndRef): void {
    const d = this.draft;
    if (!d) return;
    this.draft = null;
    const snap = captureSnapshot();
    const id = p3.commitRun(
      { from: d.from, pts: d.pts.slice(), to },
      { start: d.extend, end: land },
      { bend: this.newBend, service: this.newService },
    );
    pushCapturedUndo(snap);
    S.selected = [id];
    emit('doc');
    emit('selection');
    this.invalidate();
    this.notify();
  }

  /** Finish the run at the last free point, without joining anything. */
  finishPipe(): void {
    const d = this.draft;
    if (!d) return;
    if (!d.pts.length) {
      this.cancelPipe();
      return;
    }
    const end = d.pts[d.pts.length - 1];
    d.pts = d.pts.slice(0, -1);
    this.commitDraft({ p: end });
  }

  /** True while the run being drawn is the continuation of an existing one. */
  continuingRun(): boolean {
    return !!this.draft?.extend;
  }

  cancelPipe(): void {
    if (!this.draft) return;
    this.draft = null;
    this.markDirty();
    this.notify();
  }

  setTool(t: Tool3D): void {
    if (t === this.tool) return;
    this.cancelPipe();
    this.cancelDim();
    this.tool = t;
    if (this.canvas) this.canvas.style.cursor = t === 'pipe' ? 'crosshair' : 'default';
    this.markDirty();
    this.notify();
  }

  getTool(): Tool3D {
    return this.tool;
  }

  setAxisMode(m: p3.AxisMode): void {
    this.axisMode = m;
    this.markDirty();
  }

  getAxisMode(): p3.AxisMode {
    return this.axisMode;
  }

  /** How many vertices the run in progress has, or -1 when idle. */
  draftLength(): number {
    return this.draft ? this.draftPoints().length : -1;
  }

  setNewBend(b: BendStyle): void {
    this.newBend = b;
  }

  getNewBend(): BendStyle {
    return this.newBend;
  }

  setNewService(v: boolean): void {
    this.newService = v;
  }

  getNewService(): boolean {
    return this.newService;
  }

  // ── Editing the selection ──────────────────────────────────────────────

  /** Elbow radius on the selected runs. */
  setSelectionBend(b: BendStyle): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const q = p3.findPipe3(id);
      if (q) {
        q.bend = b;
        any = true;
        continue;
      }
      const cn = S.connections.find((c) => c.id === id);
      if (cn) {
        cn.bend3 = b;
        any = true;
      }
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /**
   * Delete the selected pipework.
   *
   * A pipe DRAWN in 3D is genuinely removed. A pipe derived from a 2D
   * connector is only hidden here — the connector stays in the drawing, which
   * is the whole reason the 3D view is a separate place to work.
   */
  deleteSelectedPipes(): boolean {
    const snap = captureSnapshot();
    let any = false;
    for (const id of [...S.selected]) {
      if (p3.deletePipe3(id)) {
        any = true;
        continue;
      }
      const cn = S.connections.find((c) => c.id === id);
      if (cn && !cn.hide3) {
        cn.hide3 = true;
        any = true;
      }
    }
    if (!any) return false;
    S.selected = [];
    pushCapturedUndo(snap);
    emit('doc');
    emit('selection');
    this.invalidate();
    return true;
  }

  /** Bring a hidden 2D connector's pipe back into the 3D view. */
  showSelectedPipes(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const cn = S.connections.find((c) => c.id === id);
      if (cn?.hide3) {
        delete cn.hide3;
        any = true;
      }
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /**
   * Nudge the selection with the keyboard.
   *
   * Movement is in WORLD axes rather than camera-relative: at a three-quarter
   * view a camera-relative nudge sends equipment off on a diagonal, and the
   * whole point of arrow keys is to place something exactly.
   */
  nudge(dx: number, dy: number, dz: number): boolean {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const q = p3.findPipe3(id);
      if (q) {
        for (const v of p3.editableVertices(q))
          p3.movePipe3Point(q, v.index, { x: v.p.x + dx, y: v.p.y + dy, z: v.p.z + dz });
        any = true;
        continue;
      }
      const cn = S.connections.find((c) => c.id === id);
      if (cn?.wp3?.length) {
        cn.wp3 = cn.wp3.map((w) => ({ x: w.x + dx, y: w.y + dy, z: w.z + dz }));
        any = true;
        continue;
      }
      const s = S.shapes.find((x) => x.id === id);
      if (!s || s.locked) continue;
      const pl = placeOf(s);
      if (dx || dz) setPlan(s, pl.x + dx, pl.z + dz);
      if (dy) setElevation(s, pl.y + dy, false);
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
    return any;
  }

  /**
   * Delete one leg of a drawn run, splitting it where the leg was.
   *
   * Only applies to pipes drawn in 3D. A 2D connector has to join two ports by
   * definition, so cutting a hole in the middle of one is not a thing it can
   * mean — hide the whole run instead.
   */
  deleteLeg(pipeId: string, legIndex: number): boolean {
    const q = p3.findPipe3(pipeId);
    if (!q) return false;
    const snap = captureSnapshot();
    const replacements = p3.removeLeg(q, legIndex);
    const i = S.pipes3.indexOf(q);
    if (i < 0) return false;
    S.pipes3.splice(i, 1, ...replacements);
    S.selected = replacements.map((r) => r.id);
    pushCapturedUndo(snap);
    emit('doc');
    emit('selection');
    this.invalidate();
    return true;
  }




  // ── Navigation ─────────────────────────────────────────────────────────

  /**
   * The world point under the cursor.
   *
   * Whatever geometry is frontmost there, at its real depth. With nothing
   * under the cursor it falls back to the ground plane, and failing that to
   * the plane through the camera target — so this always returns somewhere
   * sensible to zoom toward.
   */
  private pointUnderCursor(px: number, py: number): Vec3 {
    const r = this.rayAt(px, py);
    const depth = this.renderer.pickDepth(px, py);
    if (depth !== null) {
      const fwd = this.camera.forward();
      // `depth` is measured along the view axis; the ray is a unit vector that
      // may be off-axis, so convert before stepping along it.
      const along = Math.max(0.2, r.d.x * fwd.x + r.d.y * fwd.y + r.d.z * fwd.z);
      const t = depth / along;
      return { x: r.o.x + r.d.x * t, y: r.o.y + r.d.y * t, z: r.o.z + r.d.z * t };
    }
    const ground = rayPlanePoint(r.o, r.d, v3(0, 0, 0), v3(0, 1, 0));
    if (ground) return ground;
    const t = this.camera.distance;
    return { x: r.o.x + r.d.x * t, y: r.o.y + r.d.y * t, z: r.o.z + r.d.z * t };
  }

  /** Wheel zoom, anchored on whatever the cursor is over. */
  private wheelZoom(px: number, py: number, deltaY: number, deltaMode: number): void {
    const at = this.pointUnderCursor(px, py);
    this.camera.zoomAt(at, wheelZoomFactor(deltaY, deltaMode));
    this.markDirty();
  }

  /**
   * Everything under the cursor, and where we are in that stack.
   *
   * Clicking the same spot again steps to the next thing behind — which on a
   * drawing where a valve sits behind two pipes and a handrail is the only
   * practical way to reach it.
   */
  private cycle: { x: number; y: number; i: number } | null = null;

  private pickCycling(px: number, py: number): SceneObject | null {
    const all = this.renderer.pickAll(px, py, this.scene).filter((o) => o.kind !== 'structure');
    if (!all.length) {
      this.cycle = null;
      return null;
    }
    const same = this.cycle && Math.hypot(this.cycle.x - px, this.cycle.y - py) < 6;
    const i = same ? (this.cycle!.i + 1) % all.length : 0;
    this.cycle = { x: px, y: py, i };
    return all[i];
  }

  /** How many things are stacked under the last click, and which one is active. */
  pickStack(): { count: number; index: number } {
    return { count: 0, index: 0 };
  }

  /** Frame an object without needing it selected first. */
  private focusAt(px: number, py: number): void {
    const hit = this.renderer.pick(px, py, this.scene);
    if (!hit || hit.kind === 'structure') return;
    S.selected = [hit.id];
    markDirty();
    emit('selection');
    this.fitSelection();
  }

  // -- Dimension tool -----------------------------------------------------

  /** First point of the dimension being placed, if any. */
  private dimDraft: { from: Pipe3Node; preview: SnapResult | null } | null = null;

  /** A click while the dimension tool is running: set the first or second point. */
  private dimClick(px: number, py: number): void {
    const hit = this.snapAt(px, py);
    if (!this.dimDraft) {
      this.dimDraft = { from: this.nodeFrom(hit), preview: hit };
      this.markDirty();
      this.notify();
      return;
    }
    const ends = d3.endsOfDim({ id: 'probe', from: this.dimDraft.from, to: this.nodeFrom(hit) });
    // A second click in the same place would make a zero-length dimension.
    if (ends && Math.hypot(ends.b.x - ends.a.x, ends.b.y - ends.a.y, ends.b.z - ends.a.z) < 2) return;

    const snap = captureSnapshot();
    const dim: Dim3 = { id: d3.nextDimId(), from: this.dimDraft.from, to: this.nodeFrom(hit) };
    S.dims3.push(dim);
    this.dimDraft = null;
    pushCapturedUndo(snap);
    S.selected = [dim.id];
    emit('doc');
    emit('selection');
    this.invalidate();
    this.notify();
  }

  private updateDimPreview(px: number, py: number): void {
    if (!this.dimDraft) return;
    this.dimDraft.preview = this.snapAt(px, py);
    this.markDirty();
  }

  cancelDim(): void {
    if (!this.dimDraft) return;
    this.dimDraft = null;
    this.markDirty();
    this.notify();
  }

  /** True once the first point is down but the second is not. */
  dimPending(): boolean {
    return !!this.dimDraft;
  }

  /**
   * The run a leg id points at: pipework drawn in 3D, or a 2D connector routed
   * into it. Both can carry captions; only the first can be driven to a length,
   * because a connector's route is dictated by where its equipment sits.
   */
  private legTarget(id: string): { pipe?: Pipe3; conn?: Connection; seg: number } | null {
    if (!d3.isLegId(id)) return null;
    const runId = d3.legRun(id);
    const seg = d3.legSeg(id);
    const pipe = p3.findPipe3(runId);
    if (pipe) return { pipe, seg };
    const conn = S.connections.find((c) => c.id === runId);
    if (conn) return { conn, seg };
    return null;
  }

  /**
   * The measured length of a leg, in world units, or 0.
   * Read off the scene's own dimension list: it already resolved every leg of
   * every run for the drawing pass, including the connector routes that are not
   * reconstructible from the document alone.
   */
  private legLength(id: string): number {
    const runId = d3.legRun(id);
    const seg = d3.legSeg(id);
    const leg = this.scene.dims.find((l) => l.id === runId && l.seg === seg);
    if (!leg) return 0;
    return Math.hypot(leg.b.x - leg.a.x, leg.b.y - leg.a.y, leg.b.z - leg.a.z);
  }

  /**
   * The stretch of its run a leg occupies, as the scene measured it.
   *
   * Taken from the drawn leg rather than recomputed: a connector's route is
   * derived, and the list of drawn legs skips the ones too short to dimension
   * and the ones switched off — so an index into that list is not an index into
   * the run, and rebuilding a polyline from it would shift every note along by
   * however many legs were missing.
   */
  private legSpan(id: string): { t0: number; t1: number } | null {
    const leg = this.scene.dims.find((l) => l.id === d3.legRun(id) && l.seg === d3.legSeg(id));
    return leg ? { t0: leg.t0, t1: leg.t1 } : null;
  }

  /** The note on a leg, if it carries one. */
  private legNote(id: string): LegNote | undefined {
    const t = this.legTarget(id);
    const sp = this.legSpan(id);
    if (!t || !sp) return undefined;
    return p3.noteInSpan((t.pipe ?? t.conn!).legNotes3, sp.t0, sp.t1);
  }

  /** The caption typed on a leg, or '' for the measured value. */
  private legText(id: string): string {
    return this.legNote(id)?.text ?? '';
  }

  /** Change the note on every selected leg. Returns whether anything changed. */
  private editLegNotes(patch: Partial<LegNote>): boolean {
    let any = false;
    for (const id of S.selected) {
      const t = this.legTarget(id);
      const sp = this.legSpan(id);
      if (!t || !sp) continue;
      p3.setNoteInSpan(t.pipe ?? t.conn!, sp.t0, sp.t1, patch);
      any = true;
    }
    return any;
  }

  /**
   * A stable key for whichever dimension is selected.
   *
   * The HUD uses it to decide when to re-seed its caption box, so it must not
   * change as the caption is typed — which is exactly what went wrong when the
   * key was built from the label.
   */
  selectedDimKey(): string {
    for (const id of S.selected) if (d3.isLegId(id) && this.legTarget(id)) return id;
    for (const id of S.selected) if (d3.findDim3(id)) return id;
    return '';
  }

  /** Why the selected leg's length cannot be typed in, or '' when it can. */
  legBlockedReason(): string {
    for (const id of S.selected) {
      const t = this.legTarget(id);
      if (!t) continue;
      if (!t.pipe) return 'this leg belongs to a 2D connector — its route follows the equipment';
      return p3.whyNotDrivable(t.pipe, t.seg) ?? '';
    }
    return '';
  }

  /** Set, or with an empty string clear, the text on the selected dimensions. */
  setDimText(text: string): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const t = this.legTarget(id);
      const sp = t ? this.legSpan(id) : null;
      if (t && sp) {
        p3.setNoteInSpan(t.pipe ?? t.conn!, sp.t0, sp.t1, { text: text === '' ? undefined : text });
        any = true;
        continue;
      }
      const d = d3.findDim3(id);
      if (!d) continue;
      if (text === '') delete d.text;
      else d.text = text;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /**
   * Clear the caption on the selected legs, back to the measured length.
   * Distinct from deleting the figure: one restores the measurement, the other
   * removes the dimension.
   */
  clearLegCaption(): boolean {
    const snap = captureSnapshot();
    if (!this.editLegNotes({ text: undefined })) return false;
    pushCapturedUndo(snap);
    emit('doc');
    this.invalidate();
    return true;
  }

  /** How many leg figures are switched off across the whole drawing. */
  hiddenLegDims(): number {
    let n = 0;
    for (const q of S.pipes3) n += q.legNotes3?.filter((x) => x.off).length ?? 0;
    for (const c of S.connections) n += c.legNotes3?.filter((x) => x.off).length ?? 0;
    return n;
  }

  /** Dimension every leg again, keeping the captions people typed. */
  showLegDims(): boolean {
    const snap = captureSnapshot();
    let any = false;
    for (const q of S.pipes3) if (p3.showAllLegs(q)) any = true;
    for (const c of S.connections) if (p3.showAllLegs(c)) any = true;
    if (!any) return false;
    pushCapturedUndo(snap);
    emit('doc');
    this.invalidate();
    return true;
  }

  /** Put the selected dimensions on the other side of what they measure. */
  flipDim(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const d = d3.findDim3(id);
      if (!d) continue;
      if (d.flip) delete d.flip;
      else d.flip = true;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Move the selected dimensions further from, or nearer to, what they measure. */
  nudgeDimOffset(delta: number): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const d = d3.findDim3(id);
      if (!d) continue;
      d.off = Math.max(10, Math.min(160, (d.off ?? d3.DEFAULT_OFF) + delta));
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /**
   * Delete the selected dimensions.
   *
   * On a leg figure this removes the FIGURE — the leg stops being dimensioned,
   * which is what a draughtsman means by deleting a dimension: a drawing
   * dimensions what matters, not every straight on the sheet. It never deletes
   * the pipe; that would be a trap on a panel about annotations. The run
   * remembers, so `showLegDims` brings them back.
   */
  deleteSelectedDims(): boolean {
    const snap = captureSnapshot();
    let any = false;
    const legs = this.editLegNotes({ off: true });
    for (const id of [...S.selected]) {
      if (d3.isLegId(id)) continue;
      if (d3.deleteDim3(id)) any = true;
    }
    if (!any && !legs) return false;
    if (any) S.selected = [];
    pushCapturedUndo(snap);
    emit('doc');
    emit('selection');
    this.invalidate();
    return true;
  }


  /**
   * Drive a dimension to a length instead of reading one off the model.
   *
   * The far end is moved along the line the dimension already points down until
   * it measures what you typed — so "this run is 45 m" becomes a statement the
   * geometry obeys, not a caption sitting next to geometry that says otherwise.
   * A pinned far end is detached first, because it cannot move while anchored.
   */
  setDimLength(mm: number): void {
    if (!Number.isFinite(mm) || mm <= 0) return;
    const world = mm / S.scaleMm;
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      // A leg figure drives the PIPE, not a dimension object: the number the
      // reader typed becomes the geometry rather than a caption sitting beside
      // geometry that says otherwise.
      const t = this.legTarget(id);
      if (t) {
        if (t.pipe && p3.driveLegLength(t.pipe, t.seg, world)) any = true;
        continue;
      }
      const d = d3.findDim3(id);
      if (!d) continue;
      const e = d3.endsOfDim(d);
      if (!e) continue;
      const dx = e.b.x - e.a.x;
      const dy = e.b.y - e.a.y;
      const dz = e.b.z - e.a.z;
      const len = Math.hypot(dx, dy, dz);
      // A zero-length dimension has no direction to grow along; send it out
      // along +X so there is something to grab and re-aim.
      const ux = len > 1e-6 ? dx / len : 1;
      const uy = len > 1e-6 ? dy / len : 0;
      const uz = len > 1e-6 ? dz / len : 0;
      d3.detachEnd(d, 1);
      d3.moveDimEnd(d, 1, {
        x: e.a.x + ux * world,
        y: e.a.y + uy * world,
        z: e.a.z + uz * world,
      });
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }


  // ── Pipe colour ────────────────────────────────────────────────────────

  /**
   * Paint the selected pipework.
   *
   * A pipe drawn in 3D simply takes the colour. A pipe derived from a 2D
   * connector takes a 3D-ONLY override, so colouring the pipe never repaints
   * the line on the schematic — the two mean different things and are allowed
   * to disagree.
   */
  setPipeColor(hex: string): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const q = p3.findPipe3(id);
      if (q) {
        q.color = hex;
        any = true;
        continue;
      }
      const cn = S.connections.find((c) => c.id === id);
      if (cn) {
        cn.color3 = hex;
        any = true;
      }
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Drop the 3D colour so the run goes back to bare steel, or to its 2D colour. */
  resetPipeColor(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const q = p3.findPipe3(id);
      if (q?.color) {
        delete q.color;
        any = true;
        continue;
      }
      const cn = S.connections.find((c) => c.id === id);
      if (cn?.color3) {
        delete cn.color3;
        any = true;
      }
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  // ── Zone boxes ─────────────────────────────────────────────────────────

  /**
   * Switch a zone box between a painted floor area, a standing wall, and a
   * room. Turning one into a wall gives it a height that reaches the top floor,
   * which is almost always what a tower face is for.
   */
  setZoneMode(mode: 'floor' | 'wall' | 'room'): void {
    const snap = captureSnapshot();
    let any = false;
    const top = S.levels[S.levels.length - 1]?.elevation ?? 0;
    for (const id of S.selected) {
      const sh = S.shapes.find((x) => x.id === id);
      if (!sh || sh.type !== 'zonebox') continue;
      if (mode === 'floor') {
        delete sh.mode3;
        delete sh.wallSide3;
        delete sh.wallH3;
      } else {
        sh.mode3 = mode;
        if (sh.wallH3 === undefined)
          sh.wallH3 = mode === 'wall' ? Math.max(120, Math.round(top + 120)) : Math.max(40, sh.h * 1.2);
        if (mode === 'wall' && !sh.wallSide3) sh.wallSide3 = 'N';
      }
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Move a wall onto a different edge of its zone box. */
  setWallSide(side: PortKey): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const sh = S.shapes.find((x) => x.id === id);
      if (!sh || sh.type !== 'zonebox') continue;
      sh.wallSide3 = side;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Set the height of a wall or room. */
  setWallHeight(h: number): void {
    if (!Number.isFinite(h)) return;
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const sh = S.shapes.find((x) => x.id === id);
      if (!sh || sh.type !== 'zonebox') continue;
      sh.wallH3 = Math.max(10, Math.min(4000, Math.round(h)));
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  // ── Showing and hiding ─────────────────────────────────────────────────

  /**
   * Hide the selected equipment in 3D.
   *
   * The 2D drawing keeps every symbol — this only clears the 3D view of things
   * that get in the way of whatever is being shown. Pipework serving hidden
   * equipment goes with it, since a run terminating on nothing looks broken.
   */
  hideSelectedShapes(): boolean {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const sh = S.shapes.find((x) => x.id === id);
      if (!sh || sh.hide3) continue;
      sh.hide3 = true;
      any = true;
    }
    if (!any) return false;
    S.selected = [];
    pushCapturedUndo(snap);
    emit('doc');
    emit('selection');
    this.invalidate();
    return true;
  }

  /** Bring everything hidden back into the 3D view. */
  showAllHidden(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const sh of S.shapes)
      if (sh.hide3) {
        delete sh.hide3;
        any = true;
      }
    for (const cn of S.connections)
      if (cn.hide3) {
        delete cn.hide3;
        any = true;
      }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** How many things are currently hidden from this view. */
  hiddenCount(): number {
    return (
      S.shapes.filter((sh) => sh.hide3).length + S.connections.filter((cn) => cn.hide3).length
    );
  }

  /**
   * Turn the access platform under the selected equipment on or off.
   * Per ITEM, not per floor: a hopper bolted onto the machine below it needs no
   * steelwork even when its neighbours on the same level do.
   */
  toggleSelectionStructure(): void {
    const snap = captureSnapshot();
    const shapes = S.selected
      .map((id) => S.shapes.find((x) => x.id === id))
      .filter((x): x is Shape => !!x);
    if (!shapes.length) return;
    const turnOff = !shapes.every((sh) => sh.noStruct3);
    for (const sh of shapes) {
      if (turnOff) sh.noStruct3 = true;
      else delete sh.noStruct3;
    }
    pushCapturedUndo(snap);
    emit('doc');
    this.invalidate();
  }

  /**
   * Pin the selected shapes to a particular 3D model, or clear the pin so their
   * form is inferred from type and label again.
   *
   * This is the difference between a symbol that happens to look right and one
   * that is recorded as being that thing: the pin is stored on the shape, so it
   * survives renaming the item, reusing a built-in symbol for something else,
   * and reopening the saved drawing.
   */
  setSelectionModel(key: string): void {
    const snap = captureSnapshot();
    const shapes = S.selected
      .map((id) => S.shapes.find((x) => x.id === id))
      .filter((x): x is Shape => !!x);
    if (!shapes.length) return;
    for (const sh of shapes) {
      if (key) sh.model3 = key;
      else delete sh.model3;
    }
    pushCapturedUndo(snap);
    emit('doc');
    this.invalidate();
  }

  // ── Levels ─────────────────────────────────────────────────────────────

  getLevels(): Level[] {
    return S.levels;
  }

  /** Re-derive floors and re-assign everything from scratch. Undoable. */
  arrangeByLevel(): void {
    const snap = captureSnapshot();
    levels.autoAssign(true);
    pushCapturedUndo(snap);
    emit('doc');
    this.invalidate();
    this.fitView();
  }

  addLevel(): void {
    const snap = captureSnapshot();
    levels.addLevel();
    pushCapturedUndo(snap);
    this.invalidate();
  }

  removeLevel(id: string): void {
    const snap = captureSnapshot();
    if (levels.removeLevel(id)) pushCapturedUndo(snap);
    this.invalidate();
  }

  renameLevel(id: string, name: string): void {
    levels.renameLevel(id, name);
    this.invalidate();
  }

  setLevelElevation(id: string, y: number): void {
    levels.setLevelElevation(id, y);
    this.invalidate();
  }

  /** Move the selection onto a floor. */
  setSelectionLevel(id: string): void {
    const snap = captureSnapshot();
    levels.assignSelectionTo(id, S.selected);
    pushCapturedUndo(snap);
    emit('doc');
    this.invalidate();
  }

  /** Nudge the selection's height without snapping it to a floor. */
  nudgeSelection(dy: number): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const s = S.shapes.find((x) => x.id === id);
      if (!s || s.locked) continue;
      setElevation(s, placeOf(s).y + dy, false);
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Drop the selection's 3D overrides so it follows the 2D drawing again. */
  resetSelectionPlan(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const s = S.shapes.find((x) => x.id === id);
      if (!s) continue;
      resetPlan(s);
      delete s.dz;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  // ── Pipe re-routing ────────────────────────────────────────────────────

  /** Flip which horizontal axis the selected runs turn along first. */
  flipPipeAxis(): void {
    const byId = new Map<string, Shape>();
    S.shapes.forEach((s) => byId.set(s.id, s));
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const cn = S.connections.find((c) => c.id === id);
      if (!cn) continue;
      const ends = endsOf(cn, byId);
      if (!ends) continue;
      flipTurnAxis(cn, ends[0], ends[1]);
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Send the selected runs back to automatic routing. */
  resetPipeRoute(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const cn = S.connections.find((c) => c.id === id);
      if (!cn) continue;
      resetRoute3(cn);
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /**
   * What the balance makes of one item, ready to print.
   *
   * A run gets the throughput on it; a machine gets what passes through it,
   * with what it feeds in itself called out when it feeds anything. Empty
   * before anybody has typed a rate anywhere, so a drawing without a balance
   * shows no balance.
   */
  private rateReadout(id: string): string {
    if (!id) return '';
    const bal = computeBalance();
    if (!bal.any) return '';
    if (bal.looped.has(id)) return 'in a loop';
    const onStream = bal.stream.get(id);
    if (onStream !== undefined) return formatRate(onStream);
    const n = bal.node.get(id);
    if (!n) return '';
    const through = n.in + n.added;
    if (!through) return '';
    return n.added && n.in ? `${formatRate(through)} (feeds ${formatRate(n.added)})` : formatRate(through);
  }

  /**
   * Identity of the first selected item, for the HUD's text boxes.
   *
   * They re-seed themselves when this CHANGES, so it has to be something typing
   * cannot alter — the throughput box used to be re-seeded from its own value,
   * which meant every keystroke looked like a fresh selection.
   */
  selectedKey(): string {
    return S.selected[0] ?? '';
  }

  /** Everything the HUD needs to know about the current selection. */
  getSelection3D(): Selection3D {
    const shapes = S.selected
      .map((id) => S.shapes.find((s) => s.id === id))
      .filter((s): s is Shape => !!s);
    const conns = S.selected.filter((id) => S.connections.some((c) => c.id === id));
    const drawn = S.selected.map((id) => p3.findPipe3(id)).filter(Boolean) as Pipe3[];
    const dims = S.selected.map((id) => d3.findDim3(id)).filter(Boolean) as Dim3[];
    // A selected leg figure is not a document object, so it has to be answered
    // for before the "nothing selected" test below, which only knows how to
    // look things up.
    const legIds = S.selected.filter((id) => d3.isLegId(id) && !!this.legTarget(id));
    const zones = shapes.filter((sh) => sh.type === 'zonebox');
    // Whatever the first selected run is painted, and whether that is its own
    // 3D colour or one inherited from the 2D connector.
    const firstPipeColour = (() => {
      if (drawn[0]) return { value: drawn[0].color ?? '', own: !!drawn[0].color };
      const cn = S.connections.find((c) => c.id === conns[0]);
      if (!cn) return { value: '', own: false };
      return { value: cn.color3 ?? cn.color ?? '', own: !!cn.color3 };
    })();
    // What the first selected run carries, and whether that is pinned or read
    // off the drawing. The shape index is built only when there IS a run to ask
    // about — this runs on every HUD sync, and walking every shape to answer a
    // question about equipment would be pure waste.
    const firstFlow = ((): {
      pinned: FlowMedium | 'auto';
      medium: FlowMedium;
      reversed: boolean;
    } => {
      const none = { pinned: 'auto' as const, medium: 'none' as const, reversed: false };
      if (!drawn.length && !conns.length) return none;
      const byId = shapeIndex(S.shapes);
      if (drawn[0])
        return {
          pinned: drawn[0].flow3 ?? 'auto',
          medium: mediumOfPipe3(drawn[0], byId),
          reversed: directionOfPipe3(drawn[0]) === 'reverse',
        };
      const cn = S.connections.find((c) => c.id === conns[0]);
      return cn
        ? {
            pinned: cn.flow3 ?? 'auto',
            medium: mediumOfConnection(cn, byId),
            reversed: directionOfConnection(cn) === 'reverse',
          }
        : none;
    })();
    if (!shapes.length && !conns.length && !drawn.length && !dims.length && legIds.length) {
      const id = legIds[0];
      const t = this.legTarget(id)!;
      return {
        kind: 'conn', count: legIds.length, label: '', levelId: '', elevation: 0,
        moved: false, rerouted: false, bends: 0, isPipe3: false, bend: 'long', shownIn3D: true,
        hidden: false, noStruct: false, model3: '', runLength: 0,
        isDim: true,
        isLeg: true,
        legHidden: !!this.legNote(id)?.off,
        legDrivable: !!t.pipe && !p3.whyNotDrivable(t.pipe, t.seg),
        dimText: this.legText(id),
        dimLength: this.legLength(id),
        dimFlip: false,
        isZone: false, zoneMode: 'floor', wallSide: 'N', wallH: 0,
        pipeColor: '', pipeColorOwn: false, flowPinned: 'auto', flowMedium: 'none',
        flowReversed: false, rate: 0, rateReadout: '', shapeId: '',
      };
    }
    if (!shapes.length && !conns.length && !drawn.length && !dims.length)
      return {
        kind: 'none', count: 0, label: '', levelId: '', elevation: 0,
        moved: false, rerouted: false, bends: 0, isPipe3: false, bend: 'long', shownIn3D: true,
        hidden: false, noStruct: false, model3: '', runLength: 0,
        isDim: false, isLeg: false, legDrivable: false, legHidden: false,
        dimText: '', dimLength: 0, dimFlip: false,
        isZone: false, zoneMode: 'floor', wallSide: 'N', wallH: 0,
        pipeColor: '', pipeColorOwn: false, flowPinned: 'auto', flowMedium: 'none',
        flowReversed: false, rate: 0, rateReadout: '', shapeId: '',
      };
    const kind =
      shapes.length && (conns.length || drawn.length) ? 'mixed' : shapes.length ? 'shape' : 'conn';
    const lv = shapes.length ? (shapes[0].level ?? S.levels[0]?.id ?? '') : '';
    const sameLevel = shapes.every((s) => (s.level ?? S.levels[0]?.id) === lv);
    const first = shapes[0];
    const total = shapes.length + conns.length + drawn.length + dims.length;
    return {
      kind,
      count: total,
      label:
        total > 1
          ? `${total} selected`
          : first
            ? first.label || first.type
            : drawn.length
              ? p3.describePipe3(drawn[0])
              : dims.length
                ? d3.describeDim3(dims[0], S.scaleMm)
                : 'pipe run',
      levelId: sameLevel ? lv : '',
      elevation: first ? Math.round(placeOf(first).y) : 0,
      moved: shapes.some(hasOverride),
      rerouted: conns.some((id) => {
        const cn = S.connections.find((c) => c.id === id);
        return !!cn && isRerouted(cn);
      }),
      bends: conns.reduce((n2, id) => {
        const cn = S.connections.find((c) => c.id === id);
        return n2 + (cn ? bendCount(cn) : 0);
      }, drawn.reduce((n2, q) => n2 + q.pts.length, 0)),
      isPipe3: drawn.length > 0,
      bend:
        drawn[0]?.bend ??
        S.connections.find((c) => c.id === conns[0])?.bend3 ??
        'long',
      shownIn3D: !conns.some((id) => S.connections.find((c) => c.id === id)?.hide3),
      hidden: shapes.length > 0 && shapes.every((sh) => !!sh.hide3),
      noStruct: shapes.length > 0 && shapes.every((sh) => !!sh.noStruct3),
      model3:
        shapes.length > 0 && shapes.every((sh) => sh.model3 === shapes[0].model3)
          ? (shapes[0].model3 ?? '')
          : '',
      runLength: this.selectedRunLength(),
      isDim: dims.length > 0,
      isLeg: false,
      legDrivable: false,
      legHidden: false,
      dimText: dims[0]?.text ?? '',
      dimLength: dims[0] ? d3.measure(dims[0]) : 0,
      dimFlip: !!dims[0]?.flip,
      isZone: zones.length > 0,
      zoneMode: zones[0]?.mode3 ?? 'floor',
      wallSide: zones[0]?.wallSide3 ?? 'N',
      wallH: Math.round(zones[0]?.wallH3 ?? (zones[0] ? zones[0].h * 3 : 0)),
      pipeColor: firstPipeColour.value,
      pipeColorOwn: firstPipeColour.own,
      flowPinned: firstFlow.pinned,
      flowMedium: firstFlow.medium,
      flowReversed: firstFlow.reversed,
      rate: (first ?? drawn[0] ?? S.connections.find((c) => c.id === conns[0]))?.rate ?? 0,
      rateReadout: this.rateReadout(first?.id ?? drawn[0]?.id ?? conns[0] ?? ''),
      shapeId: shapes[0]?.id ?? '',
    };
  }

  /** Add a bend to every selected run. */
  addBendToSelection(): void {
    const byId = new Map<string, Shape>();
    S.shapes.forEach((s) => byId.set(s.id, s));
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const cn = S.connections.find((c) => c.id === id);
      if (!cn) continue;
      const ends = endsOf(cn, byId);
      if (!ends) continue;
      addBend(cn, ends[0], ends[1]);
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Remove the last bend from every selected run. */
  removeBendFromSelection(): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const cn = S.connections.find((c) => c.id === id);
      if (!cn || !cn.wp3?.length) continue;
      removeBend(cn, cn.wp3.length - 1);
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /** Show or hide the steelwork for one floor. */
  setLevelStructure(id: string, on: boolean): void {
    const snap = captureSnapshot();
    setLevelStructure(id, on);
    pushCapturedUndo(snap);
    this.invalidate();
  }

  /** Whether a floor is currently drawing any steelwork. */
  levelStructureState(id: string): { on: boolean; any: boolean } {
    const l = S.levels.find((x) => x.id === id);
    if (!l) return { on: false, any: false };
    return { on: l.structure !== false, any: levelHasStructure(l) };
  }

  /**
   * Developed length of every selected run — the figure you would order pipe by.
   * Measured along the centre-line, so it counts the risers and the crossings,
   * not the straight-line distance between the two ends.
   */
  private selectedRunLength(): number {
    const byId = new Map<string, Shape>();
    S.shapes.forEach((sh) => byId.set(sh.id, sh));
    const walk = (pts: { x: number; y: number; z: number }[]): number => {
      let sum = 0;
      for (let i = 1; i < pts.length; i++)
        sum += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y, pts[i].z - pts[i - 1].z);
      return sum;
    };
    let total = 0;
    for (const id of S.selected) {
      const q = p3.findPipe3(id);
      if (q) {
        total += walk(p3.centreLine(q));
        continue;
      }
      const cn = S.connections.find((c) => c.id === id);
      if (!cn) continue;
      const ends = endsOf(cn, byId);
      if (ends) total += walk(routePipe3(ends[0], ends[1], cn).path);
    }
    return total;
  }

  toggleStructure(): boolean {
    setStructureVisible(!isStructureVisible());
    saveViewPrefs({ ...this.opts, structure: isStructureVisible() });
    this.invalidate();
    return isStructureVisible();
  }

  isStructureOn(): boolean {
    return isStructureVisible();
  }

  // ── Public API (called by the 3D HUD) ──────────────────────────────────

  setPreset(p: ViewPreset): void {
    this.camera.preset(p);
    this.markDirty();
  }

  setProjection(k: ProjectionKind): void {
    this.camera.kind = k;
    this.markDirty();
  }

  getProjection(): ProjectionKind {
    return this.camera.kind;
  }

  // ── The plant running ──────────────────────────────────────────────────

  /**
   * Start or stop the flow.
   *
   * Stopping leaves the slugs exactly where they were rather than resetting
   * them, so it works as a pause — freeze the view, look at where the material
   * has got to, carry on.
   *
   * @returns whether the flow is now running.
   */
  toggleFlow(): boolean {
    this.flowOn = !this.flowOn;
    // Zeroed either way: the gap while it was stopped is not travel.
    this.flowClock = 0;
    // A rebuild, not a repaint — the pipe wall is swept differently while the
    // flow is on. See `rebuild`.
    this.sceneDirty = true;
    this.markDirty();
    this.notify();
    return this.flowOn;
  }

  isFlowRunning(): boolean {
    return this.flowOn;
  }

  /** Speed multiplier on both streams. 1 is the designed rate. */
  setFlowSpeed(v: number): void {
    this.flowSpeed = Math.max(0.1, Math.min(4, v));
    this.markDirty();
  }

  getFlowSpeed(): number {
    return this.flowSpeed;
  }

  /**
   * Pin what the selected runs carry, or hand them back to the drawing.
   *
   * Pinned rather than inferred because a guess is a bad record: repainting a
   * run or renaming it would otherwise change what it is shown carrying, with
   * nothing on the drawing to say why. `'auto'` deletes the pin instead of
   * storing a value, so the run goes back to following the drawing — which is
   * not the same as pinning it to what the drawing currently implies.
   */
  setSelectionFlow(m: FlowMedium | 'auto'): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const target: Pipe3 | Connection | undefined =
        p3.findPipe3(id) ?? S.connections.find((c) => c.id === id);
      if (!target) continue;
      if (m === 'auto') delete target.flow3;
      else target.flow3 = m;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  /**
   * Turn the selected runs round, or put them back the way they were drawn.
   *
   * A connector points from one machine to another and that is the way its
   * contents travel — except on a vacuum line, which is drawn from the pump
   * that serves it and flows the other way. Nothing on a schematic can work
   * that out, so it is recorded here instead of guessed.
   *
   * Turning a run back off DELETES the field rather than storing 'forward', so
   * a drawing carries a note only where somebody actually made a decision.
   * Nothing about the geometry changes either way: the distances along the run
   * are untouched and only the slugs turn round.
   */
  /**
   * Set the throughput on the selected items, in kg/h. Zero clears it.
   *
   * On a piece of equipment this is what that item FEEDS IN; on a run it pins
   * that stream and lets the rest of its machine's output share what is left.
   * See `engine/balance`.
   */
  setSelectionRate(kgh: number): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const target: Pipe3 | Connection | Shape | undefined =
        p3.findPipe3(id) ??
        S.connections.find((c) => c.id === id) ??
        S.shapes.find((sh) => sh.id === id);
      if (!target) continue;
      // Blank and zero remove the figure rather than storing a zero: "feeds
      // nothing in" and "nobody has said" are different, and only the second
      // should let the balance work it out.
      if (Number.isFinite(kgh) && kgh > 0) target.rate = kgh;
      else delete target.rate;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  reverseSelectionFlow(on: boolean): void {
    const snap = captureSnapshot();
    let any = false;
    for (const id of S.selected) {
      const target: Pipe3 | Connection | undefined =
        p3.findPipe3(id) ?? S.connections.find((c) => c.id === id);
      if (!target) continue;
      if (on) target.flowDir3 = 'reverse';
      else delete target.flowDir3;
      any = true;
    }
    if (any) {
      pushCapturedUndo(snap);
      emit('doc');
      this.invalidate();
    }
  }

  toggle(
    key: 'grid' | 'shadows' | 'labels' | 'tags' | 'floors' | 'dims' | 'dimsSelectedOnly' | 'isolate',
  ): boolean {
    this.opts[key] = !this.opts[key];
    saveViewPrefs(this.opts);
    this.markDirty();
    return this.opts[key];
  }

  /**
   * Re-read the signed-in user's view preferences. Called after sign-in, so
   * each account gets its own HUD back rather than inheriting the last
   * person's.
   */
  reloadViewPrefs(): void {
    const prefs = loadViewPrefs();
    Object.assign(this.opts, renderPrefs());
    // The steelwork flag is read when the scene is built, not when it is drawn,
    // so applying it has to invalidate rather than just mark a repaint.
    if (isStructureVisible() !== prefs.structure) setStructureVisible(prefs.structure);
    this.invalidate();
  }

  /**
   * Millimetres per world unit — what the dimension figures are computed in.
   * A property of the drawing, so changing it makes the document unsaved and
   * lands in the autosave with everything else.
   */
  setScaleMm(v: number): void {
    S.scaleMm = Math.max(0.1, Math.min(5000, v));
    scheduleAutoSave();
    emit('doc');
    // Dimension text is baked into the scene, so a scale change rebuilds it.
    this.invalidate();
  }

  getScaleMm(): number {
    return S.scaleMm;
  }

  setHeightScale(v: number): void {
    S.heightScale = Math.max(0.25, Math.min(4, v));
    scheduleAutoSave();
    emit('doc');
    this.markDirty();
  }

  zoomIn(): void {
    this.camera.dolly(1 / 1.12);
    this.markDirty();
  }

  zoomOut(): void {
    this.camera.dolly(1.12);
    this.markDirty();
  }

  /** Frame the whole plant. */
  fitView(): void {
    if (this.sceneDirty) this.rebuild();
    const aspect = (this.wrap?.clientWidth || 1) / (this.wrap?.clientHeight || 1);
    const s = this.scene;
    const centre = {
      x: (s.min.x + s.max.x) / 2,
      y: ((s.min.y + s.max.y) / 2) * S.heightScale,
      z: (s.min.z + s.max.z) / 2,
    };
    if (s.count > 0) {
      this.camera.fitPoints(s.pos, s.count * 3, centre, aspect, S.heightScale);
    } else {
      const max = { ...s.max };
      max.y *= S.heightScale;
      this.camera.fit(s.min, max, aspect);
    }
    this.markDirty();
  }

  /** Frame the current selection, or the whole plant when nothing is selected. */
  fitSelection(): void {
    if (this.sceneDirty) this.rebuild();
    const sel = new Set(S.selected);
    const hits = this.scene.objects.filter((o) => sel.has(o.id));
    if (!hits.length) return this.fitView();
    let min = { x: Infinity, y: Infinity, z: Infinity };
    let max = { x: -Infinity, y: -Infinity, z: -Infinity };
    for (const o of hits) {
      min = { x: Math.min(min.x, o.min.x), y: Math.min(min.y, o.min.y), z: Math.min(min.z, o.min.z) };
      max = { x: Math.max(max.x, o.max.x), y: Math.max(max.y, o.max.y), z: Math.max(max.z, o.max.z) };
    }
    max.y *= S.heightScale;
    const aspect = (this.wrap?.clientWidth || 1) / (this.wrap?.clientHeight || 1);
    this.camera.fit(min, max, aspect, 1.6);
    this.markDirty();
  }

  getStats(): View3DStats {
    const types = new Set<string>();
    let shapes = 0;
    let pipes = 0;
    for (const o of this.scene.objects) {
      if (o.kind === 'shape') {
        shapes++;
        if (hasBespokeModel(o.type)) types.add(o.type);
      } else pipes++;
    }
    return {
      shapes,
      pipes,
      triangles: this.renderer.drawn,
      modelled: types.size,
      fps: Math.round(this.fps),
      levels: S.levels.length,
    };
  }

  /**
   * Whether the 3D view is on screen. `captureViews` needs a live renderer, so
   * the PDF dialog asks this before offering a 3D sheet rather than offering one
   * and quietly producing nothing.
   */
  isMounted(): boolean {
    return this.mounted;
  }

  /**
   * Render a set of views OFF SCREEN and hand them back as PNGs, for the PDF.
   *
   * A plant is the one thing a single still cannot explain, and a supplier
   * holding a printed sheet has no way to orbit it. Framed views — isometric
   * from two corners, a plan, an elevation — answer most of what they would
   * otherwise phone up to ask.
   *
   * `'current'` captures whatever the engineer has the camera on right now,
   * untouched. That view is a deliberate act — they framed it to show something
   * — so re-fitting it would throw away the only view on the sheet that somebody
   * actually composed.
   *
   * The live canvas is left exactly as it was: the camera is saved and put back,
   * the renderer is re-attached to the on-screen context, and `resize()`
   * recomputes the viewport from the wrapper. Nothing here touches the document.
   *
   * @param wCss  Composition width in CSS pixels — this sets how big text and
   *   line weights look relative to the plant, so it stays near a real viewport.
   * @param dpr   Pixel multiplier. 4 gives roughly 135 dpi in a half-A1 frame.
   */
  captureViews(
    specs: CaptureSpec[] = [
      { preset: 'iso', label: 'Isometric — south-west' },
      { preset: 'isoNE', label: 'Isometric — north-east' },
      { preset: 'top', label: 'Plan' },
      { preset: 'front', label: 'Elevation — south' },
    ],
    wCss = 520,
    hCss = 360,
    dpr = 4,
  ): CapturedView[] {
    if (!this.mounted) return [];
    if (this.sceneDirty) this.rebuild();
    if (!this.scene.count) return [];

    // Everything that has to go back afterwards.
    const cam = this.camera;
    const saved = {
      target: { ...cam.target },
      azimuth: cam.azimuth,
      elevation: cam.elevation,
      distance: cam.distance,
      kind: cam.kind,
    };

    const off = document.createElement('canvas');
    off.width = Math.round(wCss * dpr);
    off.height = Math.round(hCss * dpr);
    const offCtx = off.getContext('2d');
    if (!offCtx) return [];

    const out: CapturedView[] = [];
    try {
      this.renderer.attach(offCtx);
      this.renderer.resize(wCss, hCss, dpr);
      const aspect = wCss / hCss;
      const s = this.scene;
      const centre = {
        x: (s.min.x + s.max.x) / 2,
        y: ((s.min.y + s.max.y) / 2) * S.heightScale,
        z: (s.min.z + s.max.z) / 2,
      };

      for (const { preset, label } of specs) {
        if (preset === 'current') {
          // Put the composed view back: an earlier preset in this same run will
          // have moved the camera, and "current" has to mean what the engineer
          // was looking at, not wherever the loop left off.
          cam.target = { ...saved.target };
          cam.azimuth = saved.azimuth;
          cam.elevation = saved.elevation;
          cam.kind = saved.kind;
          // Pull back a little when the sheet is a narrower shape than the
          // screen, so nothing they framed falls off the edge of the paper.
          const screenAspect = (this.wrap?.clientWidth || 1) / (this.wrap?.clientHeight || 1);
          cam.distance = saved.distance * (aspect < screenAspect ? screenAspect / aspect : 1);
        } else {
          cam.preset(preset);
          cam.fitPoints(s.pos, s.count * 3, centre, aspect, S.heightScale);
        }
        offCtx.setTransform(1, 0, 0, 1, 0, 0);
        offCtx.clearRect(0, 0, off.width, off.height);
        this.renderer.render(
          this.scene,
          cam,
          {
            ...this.opts,
            scaleMm: S.scaleMm,
            heightScale: S.heightScale,
            // No selection highlight, no gizmo, and no flow: this is a drawing,
            // not a screenshot of somebody mid-edit. A frozen frame of the
            // animation would print as an unexplained band of stripes down
            // every line, which is worse than not printing it at all.
            selected: new Set<string>(),
            hoveredId: null,
            flow: null,
          },
          undefined,
        );
        out.push({ label, png: off.toDataURL('image/png'), wPx: off.width, hPx: off.height });
      }
    } catch {
      // A capture that fails costs the 3D sheet, not the export — and, more to
      // the point, must not cost the user their live view.
    } finally {
      cam.target = saved.target;
      cam.azimuth = saved.azimuth;
      cam.elevation = saved.elevation;
      cam.distance = saved.distance;
      cam.kind = saved.kind;
      if (this.ctx) this.renderer.attach(this.ctx);
      // Puts W/H/dpr back from the wrapper, whatever they were.
      this.resize();
      this.markDirty();
    }
    return out;
  }

  /** Export the current view as a PNG at double resolution. */
  exportPng(name: string): void {
    const canvas = this.canvas;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (name || 'PFD') + '-3D.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  }

  /** Subscribe to per-frame updates (drives the live stats readout). */
  onFrame(cb: View3DListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private hudSig = '';
  private docRev = 0;

  /**
   * Everything the 3D control bar reads, as one short string.
   *
   * The frame loop used to push a React update on every rendered frame. On a
   * full plant that re-rendered the whole HUD sixty times a second — and the
   * first thing that render does is rebuild the selection description, which
   * walks the shape and connection lists. All of it to redraw a panel whose
   * contents had not changed since the last frame. Comparing this signature
   * first costs a handful of field reads and skips the lot.
   *
   * `docRev` stands in for the drawing itself: every editing path already
   * invalidates the compiled scene, so counting those covers selection
   * contents, levels and the hidden tally without walking anything here.
   */
  private hudSignature(): string {
    return [
      this.docRev,
      S.selected.join('~'),
      this.tool,
      this.axisMode,
      this.newBend,
      this.draft ? this.draftPoints().length : -1,
      this.hiddenLegDims(),
      Math.round(this.draftLegMm()),
      Math.round(this.draftTotalMm()),
      S.scaleMm,
      this.dimPending() ? 1 : 0,
      // Whether it is running, not how far it has got: `flowTravel` changes
      // every frame, and putting it here would defeat the whole comparison.
      this.flowOn ? 1 : 0,
    ].join('|');
  }

  /** Per-frame notify, but only when the HUD would show something new. */
  private notifyIfChanged(): void {
    const sig = this.hudSignature();
    if (sig === this.hudSig) return;
    this.hudSig = sig;
    this.notify();
  }

  private notifyPending = false;
  private notify(): void {
    if (this.notifyPending || !this.listeners.size) return;
    // Coalesce to one React update per animation frame at most.
    this.notifyPending = true;
    requestAnimationFrame(() => {
      this.notifyPending = false;
      this.listeners.forEach((cb) => cb());
    });
  }
}

/** The shared 3D engine singleton. */
export const engine3d = new Engine3D();
